import moment from "moment-timezone";
import * as yup from "yup";

export type FormValidations = keyof typeof validations;

const requiredMessage = "Campo obrigatório";

const isValidBankAgency = (agency: string) => {
  return agency.replace("-", "").length <= 5;
};

const isValidBirthDate = (birthDate: string) => {
  const date = moment(birthDate, "DD/MM/YYYY", true);

  if (!date.isValid()) {
    return false;
  }

  return date.isBefore(moment());
};

const isValidMonthYearDate = (monthYearDate: string) => {
  const date = moment(monthYearDate, "MM/YY", true);

  if (!date.isValid()) {
    return false;
  }

  return date.isAfter(moment());
};

const isValidCpf = (cpf: string) => {
  const cleanCPF = cpf.replace(/\D/g, "");

  // If all digits are the same, the cpf is not valid
  if (/^(\d)\1+$/.test(cleanCPF)) {
    return false;
  }

  const calculateDigit = (base: string, factor: number): number => {
    let total = 0;
    for (const digit of base) {
      total += parseInt(digit) * factor--;
    }
    const remainder = total % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const baseCPF = cleanCPF.slice(0, 9);
  const firstDigit = calculateDigit(baseCPF, 10);
  const secondDigit = calculateDigit(baseCPF + firstDigit, 11);

  return cleanCPF.endsWith(`${firstDigit}${secondDigit}`);
};

const isValidCnpj = (cnpj: string) => {
  const cleanCNPJ = cnpj.replace(/[^\d]+/g, "");

  // If all digits are the same, the cnpj is not valid
  if (/^(\d)\1+$/.test(cleanCNPJ)) {
    return false;
  }

  const calculateDigit = (base: string, weights: number[]): number => {
    let total = 0;

    for (let i = 0; i < weights.length; i++) {
      total += parseInt(base.charAt(i)) * weights[i];
    }

    const remainder = total % 11;

    return remainder < 2 ? 0 : 11 - remainder;
  };

  const weightsFirstDigit = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weightsSecondDigit = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const firstDigit = calculateDigit(cleanCNPJ.substring(0, 12), weightsFirstDigit);
  const secondDigit = calculateDigit(cleanCNPJ.substring(0, 13), weightsSecondDigit);

  return firstDigit === parseInt(cleanCNPJ.charAt(12)) && secondDigit === parseInt(cleanCNPJ.charAt(13));
};

const isValidCpfOrCnpj = (document: string) => isValidCpf(document) || isValidCnpj(document);

export const validations = {
  none: null,
  name: yup
    .string()
    .required(requiredMessage)
    .trim()
    .min(3, "Preencha o campo corretamente")
    .matches(/(?=.*\s)/, "Preencha o campo corretamente"),
  singleName: yup.string().required(requiredMessage).trim().min(3, "Preencha o campo corretamente"),
  email: yup.string().required(requiredMessage).email("Preencha o campo corretamente"),
  password: yup
    .string()
    .required("Senha é obrigatória.")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.{8,})/,
      "A senha deve ter pelo menos 8 caracteres, contendo letras e números.",
    ),
  passwordConfirmation: yup
    .string()
    .nullable()
    .oneOf([yup.ref("password"), null], "Deve ser igual à senha."),
  phoneNumber: yup
    .string()
    .required(requiredMessage)
    .matches(/^\(\d{2}\) \d{4,5}-\d{4}/, "Preencha o campo corretamente"),
  cpf: yup
    .string()
    .required(requiredMessage)
    .matches(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, "Preencha o campo corretamente")
    .test("is-valid-cpf", "Digite um CPF válido", isValidCpf),
  cnpj: yup
    .string()
    .required(requiredMessage)
    .matches(/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/, "Preencha o campo corretamente")
    .test("is-valid-cnpj", "Digite um CNPJ válido", isValidCnpj),
  cpfOrCnpj: yup
    .string()
    .required(requiredMessage)
    .test("is-valid-cpf-or-cnpj", "Digite um CPF ou CNPJ válido", isValidCpfOrCnpj),
  birthDate: yup
    .string()
    .required(requiredMessage)
    .test("is-valid-birthdate", "Digite uma data válida", isValidBirthDate),
  monthYearDate: yup
    .string()
    .required(requiredMessage)
    .test("is-valid-birthdate", "Digite uma data válida", isValidMonthYearDate),
  bankAgency: yup
    .string()
    .required(requiredMessage)
    .test("is-valid-bank-agency", "Agência deve ter no máximo 5 dígitos", isValidBankAgency),
  postCode: yup.string().length(9, "Digite um CEP válido").required(requiredMessage),
  cardNumber: yup.string().min(14, "Digite um cartão válido").required(requiredMessage),
  required: yup.lazy((value) => {
    const input = Array.isArray(value) ? value[0] : value;
    const type = typeof input;
    const schema: yup.Schema = type === "number" ? yup.number() : yup.string();

    if (type === "boolean") {
      return yup.boolean().required("Campo obrigatório").oneOf([true], "Campo obrigatório");
    }

    let base: yup.Schema;

    if (Array.isArray(value)) {
      base = yup.array().of(schema).min(1, requiredMessage);
    } else {
      base = schema;
    }

    return base.nullable().required(requiredMessage);
  }),
};
